// Sushen Herbs — Ayurveda content library
// Content is grouped by dosha so we can personalise the wellness home & quiz.

export const DOSHAS = {
    vata: {
        key: "vata",
        name: "Vata",
        element: "Air · Ether",
        tagline: "The dosha of movement, creativity, and change.",
        color: "brand",
        summary:
            "Vata governs breath, circulation, and thought. Vata-dominant bodies are quick, agile, and imaginative — and thrive on warmth, rhythm, and grounding routines.",
        traits: ["Light frame", "Cool skin", "Fast thinker", "Loves movement"],
        careWords: ["Warm", "Rhythmic", "Grounded", "Nourishing"],
    },
    pitta: {
        key: "pitta",
        name: "Pitta",
        element: "Fire · Water",
        tagline: "The dosha of transformation, digestion, and clarity.",
        color: "warning",
        summary:
            "Pitta governs metabolism and intellect. Pitta-dominant bodies are focused, courageous, and warm — and thrive on cooling, calming, and moderation.",
        traits: ["Medium build", "Strong appetite", "Sharp focus", "Warm skin"],
        careWords: ["Cool", "Calm", "Moderate", "Refreshing"],
    },
    kapha: {
        key: "kapha",
        name: "Kapha",
        element: "Earth · Water",
        tagline: "The dosha of stability, immunity, and endurance.",
        color: "success",
        summary:
            "Kapha governs structure and immunity. Kapha-dominant bodies are steady, patient, and strong — and thrive on stimulation, warmth, and lightness.",
        traits: ["Sturdy frame", "Steady energy", "Deep sleep", "Calm mind"],
        careWords: ["Warm", "Stimulating", "Light", "Energising"],
    },
};

export function describeCombo(primary) {
    if (!primary) return null;
    if (!primary.includes("-")) return DOSHAS[primary];
    const [a, b] = primary.split("-");
    return {
        key: primary,
        name: `${DOSHAS[a].name}-${DOSHAS[b].name}`,
        element: `${DOSHAS[a].element} + ${DOSHAS[b].element}`,
        tagline: `A dual constitution blending ${DOSHAS[a].name} and ${DOSHAS[b].name}.`,
        color: DOSHAS[a].color,
        summary: `Your body carries traits of both ${DOSHAS[a].name} and ${DOSHAS[b].name}. ${DOSHAS[a].summary} ${DOSHAS[b].summary}`,
        traits: [...DOSHAS[a].traits.slice(0, 2), ...DOSHAS[b].traits.slice(0, 2)],
        careWords: [
            ...DOSHAS[a].careWords.slice(0, 2),
            ...DOSHAS[b].careWords.slice(0, 2),
        ],
    };
}

// 12-question prakriti quiz. Each option maps to one dosha.
export const QUIZ_QUESTIONS = [
    {
        id: "frame",
        q: "How would you describe your natural body frame?",
        options: [
            { dosha: "vata", label: "Light and slim — hard to gain weight" },
            { dosha: "pitta", label: "Medium — balanced and athletic" },
            { dosha: "kapha", label: "Sturdy — gains weight easily" },
        ],
    },
    {
        id: "skin",
        q: "Your skin is usually…",
        options: [
            { dosha: "vata", label: "Dry, cool, prone to roughness" },
            { dosha: "pitta", label: "Warm, sensitive, sometimes reddens" },
            { dosha: "kapha", label: "Soft, smooth, well-moisturised" },
        ],
    },
    {
        id: "hair",
        q: "Your hair tends to be…",
        options: [
            { dosha: "vata", label: "Thin, dry, sometimes frizzy" },
            { dosha: "pitta", label: "Fine, soft, oily near the scalp" },
            { dosha: "kapha", label: "Thick, wavy, glossy" },
        ],
    },
    {
        id: "appetite",
        q: "How is your appetite?",
        options: [
            { dosha: "vata", label: "Irregular — sometimes strong, sometimes gone" },
            { dosha: "pitta", label: "Strong — I get irritable when hungry" },
            { dosha: "kapha", label: "Light — I can easily skip meals" },
        ],
    },
    {
        id: "digestion",
        q: "Your digestion is usually…",
        options: [
            { dosha: "vata", label: "Variable — sometimes bloating or gas" },
            { dosha: "pitta", label: "Fast — I get heartburn if I over-do spice" },
            { dosha: "kapha", label: "Slow but steady — feels heavy after meals" },
        ],
    },
    {
        id: "sleep",
        q: "How do you sleep?",
        options: [
            { dosha: "vata", label: "Light and easily interrupted" },
            { dosha: "pitta", label: "Moderate — I wake refreshed" },
            { dosha: "kapha", label: "Deep — I could sleep for hours more" },
        ],
    },
    {
        id: "temperament",
        q: "Under pressure, I typically feel…",
        options: [
            { dosha: "vata", label: "Anxious, restless, over-thinking" },
            { dosha: "pitta", label: "Irritable, sharp, focused" },
            { dosha: "kapha", label: "Withdrawn, quiet, slow to react" },
        ],
    },
    {
        id: "weather",
        q: "Which weather do you love most?",
        options: [
            { dosha: "vata", label: "Warm and slightly humid" },
            { dosha: "pitta", label: "Cool and breezy" },
            { dosha: "kapha", label: "Warm and dry" },
        ],
    },
    {
        id: "speech",
        q: "Your natural speaking style is…",
        options: [
            { dosha: "vata", label: "Fast, animated, sometimes jumps topics" },
            { dosha: "pitta", label: "Clear, precise, gets to the point" },
            { dosha: "kapha", label: "Calm, unhurried, thoughtful" },
        ],
    },
    {
        id: "energy",
        q: "How does your energy flow through the day?",
        options: [
            { dosha: "vata", label: "Bursts of energy followed by dips" },
            { dosha: "pitta", label: "Steady and focused" },
            { dosha: "kapha", label: "Slow to start, but strong endurance" },
        ],
    },
    {
        id: "memory",
        q: "Your memory tends to be…",
        options: [
            { dosha: "vata", label: "Quick to learn, quick to forget" },
            { dosha: "pitta", label: "Sharp — I remember what matters" },
            { dosha: "kapha", label: "Slow to learn, but very long-lasting" },
        ],
    },
    {
        id: "spending",
        q: "Your relationship with money is…",
        options: [
            { dosha: "vata", label: "I spend impulsively, sometimes worry after" },
            { dosha: "pitta", label: "I spend deliberately on quality" },
            { dosha: "kapha", label: "I save carefully, spend rarely" },
        ],
    },
];

// Personalised daily rituals per dosha (Phase 1 static content; AI-generated later).
export const DAILY_RITUALS = {
    vata: [
        {
            time: "6:30 AM",
            title: "Warm water with ginger",
            body: "Kickstart digestion and calm Vata's cool, dry nature.",
        },
        {
            time: "7:00 AM",
            title: "Abhyanga — 5 min sesame oil massage",
            body: "Grounding warmth for your nervous system.",
        },
        {
            time: "10:00 PM",
            title: "Warm milk with a pinch of nutmeg",
            body: "Signals your body it's time for deep rest.",
        },
    ],
    pitta: [
        {
            time: "6:30 AM",
            title: "Room-temperature water with a squeeze of lime",
            body: "Cool and refresh without shocking your gut.",
        },
        {
            time: "7:00 AM",
            title: "10 min pranayama — Sheetali cooling breath",
            body: "Balances Pitta's inner fire.",
        },
        {
            time: "9:30 PM",
            title: "Cool coconut milk with cardamom",
            body: "Calm the day before sleep.",
        },
    ],
    kapha: [
        {
            time: "6:00 AM",
            title: "Warm water with lemon and honey",
            body: "Awakens Kapha's slower morning metabolism.",
        },
        {
            time: "7:00 AM",
            title: "20 min brisk walk or yoga",
            body: "Movement is medicine for Kapha bodies.",
        },
        {
            time: "9:00 PM",
            title: "Ginger tea with black pepper",
            body: "Keep the fire kindled through the evening.",
        },
    ],
};

// Daily tips — deterministic rotation by day-of-year so it's stable.
export const DAILY_TIPS = {
    vata: [
        "Sip warm liquids all day — cold drinks unsettle Vata.",
        "Anchor your day with 3 meals at the same times.",
        "Add ghee to one meal — it lubricates Vata's dry nature.",
        "5 min of quiet breath work does more for you than caffeine.",
    ],
    pitta: [
        "Trade your morning coffee for one glass of cool water.",
        "Take your biggest meal at noon when your fire is strongest.",
        "Cool bitter greens (arugula, kale) settle Pitta beautifully.",
        "Skip screens for 20 min after sunset — Pitta needs to wind down.",
    ],
    kapha: [
        "Warm spices (ginger, black pepper, cinnamon) are your allies.",
        "Skip heavy breakfast — a light meal wakes Kapha up.",
        "Move every 90 min. Even a 2-min stretch counts.",
        "Aim for dry sauna once a week — Kapha loves gentle heat.",
    ],
    default: [
        "Start your day with warm water, not cold. Your gut thanks you.",
        "Eat your biggest meal when the sun is highest — around noon.",
        "One handful of nuts is a snack. Two is a meal.",
        "5 min of conscious breathing = 30 min of unconscious rest.",
    ],
};

export function pickDailyTip(primary) {
    const bucket =
        (primary || "").split("-")[0] && DAILY_TIPS[(primary || "").split("-")[0]]
            ? DAILY_TIPS[(primary || "").split("-")[0]]
            : DAILY_TIPS.default;
    const day = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
    return bucket[day % bucket.length];
}
